import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SETTINGS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { handleReauth } from "@/lib/reauth";
import { Save, PauseCircle, PlayCircle, Unplug, ExternalLink, Mail, Send, AlertTriangle, CreditCard, Sparkles, BarChart3 } from "lucide-react";

const COMMON_TZS = [
  "America/Los_Angeles","America/Denver","America/Chicago","America/New_York",
  "America/Phoenix","America/Anchorage","Pacific/Honolulu","Europe/London",
  "Europe/Paris","Europe/Berlin","Europe/Madrid","Asia/Kolkata","Asia/Singapore",
  "Asia/Tokyo","Australia/Sydney","Australia/Melbourne","UTC",
];

const DAYS = [
  { v: 0, label: "Monday" }, { v: 1, label: "Tuesday" }, { v: 2, label: "Wednesday" },
  { v: 3, label: "Thursday" }, { v: 4, label: "Friday" }, { v: 5, label: "Saturday" }, { v: 6, label: "Sunday" },
];

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const detected = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const initHour = user?.scheduled_hour ?? 18;
  const initMin = user?.scheduled_minute ?? 0;
  const [time, setTime] = useState(`${String(initHour).padStart(2,"0")}:${String(initMin).padStart(2,"0")}`);
  const [tz, setTz] = useState(user?.tz || detected);
  const [busy, setBusy] = useState(false);

  // Digest state
  const [digestEnabled, setDigestEnabled] = useState(!!user?.digest_enabled);
  const [digestDay, setDigestDay] = useState((user?.digest_day ?? 6).toString());
  const [digestTime, setDigestTime] = useState(
    `${String(user?.digest_hour ?? 18).padStart(2,"0")}:${String(user?.digest_minute ?? 0).padStart(2,"0")}`
  );
  const [digestBusy, setDigestBusy] = useState(false);

  const hasSendScope = (user?.scopes_granted || []).some((s) => s.includes("gmail.send"));

  const tzOptions = useMemo(() => {
    const set = new Set(COMMON_TZS);
    if (user?.tz) set.add(user.tz);
    set.add(detected);
    return Array.from(set).sort();
  }, [user?.tz, detected]);

  const save = async () => {
    const [h,m] = time.split(":").map((x)=>parseInt(x,10));
    setBusy(true);
    try {
      await api.post("/settings/schedule", { hour: h, minute: m, tz });
      toast.success("Schedule updated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      const r = await api.post("/settings/pause", { paused: !user?.paused });
      toast.success(r.data.paused ? "Scans paused" : "Scans resumed");
      await refresh();
    } catch (e) { toast.error("Could not update"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    try {
      await api.post("/settings/disconnect");
      toast.success("Disconnected. Your Google Sheet remains in your Drive.");
      await logout();
    } catch { toast.error("Disconnect failed"); }
  };

  const saveDigest = async (enabledOverride) => {
    const enabled = enabledOverride ?? digestEnabled;
    const [dh, dm] = digestTime.split(":").map((x)=>parseInt(x,10));
    setDigestBusy(true);
    try {
      await api.post("/settings/digest", {
        enabled,
        day: enabled ? parseInt(digestDay, 10) : undefined,
        hour: enabled ? dh : undefined,
        minute: enabled ? dm : undefined,
      });
      toast.success(enabled ? "Weekly digest scheduled" : "Weekly digest turned off");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update digest");
      setDigestEnabled(!enabled);
    } finally { setDigestBusy(false); }
  };

  const sendTest = async () => {
    setDigestBusy(true);
    toast.message("Sending test digest to your inbox…");
    try {
      const r = await api.post("/settings/digest/send-test");
      toast.success(`Test sent · ${r.data.count} entries included`);
    } catch (e) {
      handleReauth(e, "Test send failed");
    } finally { setDigestBusy(false); }
  };

  const reauth = async () => {
    try {
      const r = await api.get("/auth/google/start");
      window.location.href = r.data.authorization_url;
    } catch { toast.error("Could not start re-authorization"); }
  };

  const openBillingPortal = async () => {
    try {
      const r = await api.post("/billing/portal");
      window.location.href = r.data.portal_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open billing portal"); }
  };

  const isOwner = (user?.email || "").toLowerCase() === "jennifer.chandler@gmsdk12.org";

  return (
    <div data-testid={SETTINGS.root} className="max-w-3xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-2">Account</p>
        <h1 className="font-heading text-4xl tracking-tight text-[#1C2321] font-medium">Settings</h1>
      </div>

      {/* Daily scan */}
      <section className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8 mb-6">
        <h2 className="font-heading text-xl text-[#1C2321] font-medium mb-1">Daily scan</h2>
        <p className="text-sm text-[#5D6D63] mb-6">Choose when ContactAssist scans your inbox. Times outside school hours are recommended.</p>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] block mb-2">Time</label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid={SETTINGS.timeInput}
              className="h-11 rounded-md border-[#E8E6DF]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] block mb-2">Timezone</label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger data-testid={SETTINGS.tzSelect} className="h-11 rounded-md border-[#E8E6DF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tzOptions.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <Button data-testid={SETTINGS.saveBtn} onClick={save} disabled={busy} className="h-11 rounded-md bg-[#971a8c] hover:bg-[#7d1574] text-white">
            <Save className="h-4 w-4 mr-2" /> Save schedule
          </Button>
          <Button
            data-testid={SETTINGS.pauseBtn}
            variant="outline"
            onClick={togglePause}
            disabled={busy}
            className="h-11 rounded-md border-[#E8E6DF]"
          >
            {user?.paused ? <><PlayCircle className="h-4 w-4 mr-2"/> Resume scans</> : <><PauseCircle className="h-4 w-4 mr-2"/> Pause scans</>}
          </Button>
        </div>
      </section>

      {/* Weekly digest */}
      <section className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8 mb-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div>
            <h2 className="font-heading text-xl text-[#1C2321] font-medium">Weekly digest</h2>
            <p className="text-sm text-[#5D6D63] mt-1">An email summary of the past 7 days of parent contact, delivered to your own inbox.</p>
          </div>
          <Switch
            data-testid="settings-digest-toggle"
            checked={digestEnabled}
            onCheckedChange={(v) => { setDigestEnabled(v); saveDigest(v); }}
            disabled={digestBusy || !hasSendScope}
          />
        </div>

        {!hasSendScope && (
          <div className="mt-4 p-4 rounded-md bg-[#fdf4e7] border border-[#f0c98a] text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-[#b8720a] mt-0.5" />
              <div className="flex-1">
                <p className="text-[#5c3a00] font-medium">One extra permission needed</p>
                <p className="text-[#5D6D63] mt-1">The digest is sent from your own Gmail. Sign in again to grant the <em>send email</em> scope.</p>
                <button
                  data-testid="settings-digest-reauth"
                  onClick={reauth}
                  className="mt-2 text-sm text-[#971a8c] font-medium hover:underline"
                >Re-authorize with Google →</button>
              </div>
            </div>
          </div>
        )}

        {hasSendScope && (
          <>
            <div className="grid gap-6 sm:grid-cols-2 mt-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] block mb-2">Day</label>
                <Select value={digestDay} onValueChange={setDigestDay} disabled={!digestEnabled}>
                  <SelectTrigger data-testid="settings-digest-day" className="h-11 rounded-md border-[#E8E6DF]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => <SelectItem key={d.v} value={d.v.toString()}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] block mb-2">Time</label>
                <Input
                  type="time"
                  value={digestTime}
                  onChange={(e) => setDigestTime(e.target.value)}
                  data-testid="settings-digest-time"
                  disabled={!digestEnabled}
                  className="h-11 rounded-md border-[#E8E6DF]"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Button
                data-testid="settings-digest-save"
                onClick={() => saveDigest()}
                disabled={digestBusy || !digestEnabled}
                className="h-11 rounded-md bg-[#971a8c] hover:bg-[#7d1574] text-white"
              >
                <Mail className="h-4 w-4 mr-2" /> Save digest schedule
              </Button>
              <Button
                data-testid="settings-digest-test"
                onClick={sendTest}
                disabled={digestBusy}
                variant="outline"
                className="h-11 rounded-md border-[#26abe2] text-[#26abe2] hover:bg-[#e7f5fb]"
              >
                <Send className="h-4 w-4 mr-2" /> Send test digest now
              </Button>
            </div>
          </>
        )}
      </section>

      {/* Billing */}
      {!user?.demo_mode && (
        <section data-testid="billing-section" className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl text-[#1C2321] font-medium">Subscription</h2>
              <p className="text-sm text-[#5D6D63] mt-1">
                Current status: <strong className="text-[#1C2321] capitalize">{user?.billing?.status || "none"}</strong>
                {user?.billing?.plan && <> · Plan: <strong className="text-[#1C2321] capitalize">{user.billing.plan}</strong></>}
                {user?.billing?.trial_ends_at && (
                  <> · Trial ends <span className="text-[#1C2321]">{new Date(user.billing.trial_ends_at).toLocaleDateString()}</span></>
                )}
              </p>
            </div>
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            {user?.billing?.active ? (
              <Button
                data-testid="settings-billing-portal"
                onClick={openBillingPortal}
                className="h-11 rounded-md bg-[#971a8c] hover:bg-[#7d1574] text-white"
              >
                <CreditCard className="h-4 w-4 mr-2" /> Manage billing
              </Button>
            ) : (
              <Button
                data-testid="settings-billing-start"
                onClick={() => (window.location.href = "/pricing")}
                className="h-11 rounded-md bg-[#ec197f] hover:bg-[#d1156e] text-white"
              >
                <Sparkles className="h-4 w-4 mr-2" /> Start free trial
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Owner-only tools */}
      {isOwner && (
        <section data-testid="owner-tools" className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8 mb-6">
          <h2 className="font-heading text-xl text-[#1C2321] font-medium mb-1">Owner tools</h2>
          <p className="text-sm text-[#5D6D63] mb-4">
            Admin-only utilities for your account.
          </p>
          <a
            data-testid="owner-referrals-link"
            href="/admin/referrals"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#971a8c] hover:underline"
          >
            <BarChart3 className="h-4 w-4" /> View referral analytics →
          </a>
        </section>
      )}

      {/* Google account */}
      <section className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8 mb-6">
        <h2 className="font-heading text-xl text-[#1C2321] font-medium mb-1">Google account</h2>
        <p className="text-sm text-[#5D6D63] mb-4">Signed in as <strong className="text-[#1C2321]">{user?.email}</strong></p>
        {user?.sheet_url && (
          <a href={user.sheet_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#971a8c] hover:underline text-sm">
            Open your Parent Contact Log <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </section>

      {/* Danger zone */}
      <section className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8">
        <h2 className="font-heading text-xl text-[#1C2321] font-medium mb-1">Danger zone</h2>
        <p className="text-sm text-[#5D6D63] mb-4">Disconnecting removes ContactAssist from your account and deletes your logged entries here. Your Google Sheet remains in your Drive.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid={SETTINGS.disconnectBtn} variant="outline" className="h-11 rounded-md border-[#ec197f] text-[#ec197f] hover:bg-[#fde5ef]">
              <Unplug className="h-4 w-4 mr-2" /> Disconnect Google
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect ContactAssist?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove your saved refresh token, cancel your daily scan, and delete your log entries from ContactAssist. Your Google Sheet in Drive is not touched.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={disconnect} className="bg-[#ec197f] hover:bg-[#d1156e]">Disconnect</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </div>
  );
}
